/**
 * Форматные проверки реквизитов организации (canon: company-profile-and-contacts).
 * Используются на стороне команды (редактирование профиля клиента). Сообщения — дословно из спеки;
 * это базовая фронт-валидация поверх серверной (бэкенд валидирует те же поля и шлёт details 422).
 *
 * Контрольные суммы: ИНН (10/12) и ОГРН (13/15) проверяются полностью.
 */

const digits = (s: string) => s.replace(/\D/g, '')

/** ИНН: 10 цифр (юрлицо) или 12 (ИП) с контрольной суммой. */
export function isValidInn(value: string): boolean {
  const v = digits(value)
  if (v.length !== 10 && v.length !== 12) return false
  const d = v.split('').map(Number)
  const check = (coeffs: number[]) =>
    (coeffs.reduce((sum, k, i) => sum + k * d[i], 0) % 11) % 10
  if (v.length === 10) {
    return check([2, 4, 10, 3, 5, 9, 4, 6, 8]) === d[9]
  }
  const n11 = check([7, 2, 4, 10, 3, 5, 9, 4, 6, 8])
  const n12 = check([3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8])
  return n11 === d[10] && n12 === d[11]
}

/**
 * ИНН юрлица — ровно 10 цифр (у ИП 12). От этого зависит обязательность КПП: у ИП его нет.
 * Правило вынесено сюда, чтобы форма могла показать звёздочку у подписи КПП ровно тогда,
 * когда поле действительно обязательно (common.required-fields).
 */
export function isLegalEntityInn(value: string): boolean {
  return digits(value).length === 10
}

/** ОГРН: 13 (юрлицо) или 15 (ИП) цифр с контрольной суммой. */
export function isValidOgrn(value: string): boolean {
  const v = digits(value)
  if (v.length !== 13 && v.length !== 15) return false
  const body = v.slice(0, v.length - 1)
  const control = Number(v[v.length - 1])
  const mod = v.length === 13 ? 11 : 13
  return Number(body) % mod % 10 === control
}

/** КПП: 9 цифр. */
export function isValidKpp(value: string): boolean {
  return /^\d{9}$/.test(value.trim())
}

/** ОКПО: 8 или 10 цифр. */
export function isValidOkpo(value: string): boolean {
  const v = digits(value)
  return v.length === 8 || v.length === 10
}

/** ОКТМО: 8 или 11 цифр. */
export function isValidOktmo(value: string): boolean {
  const v = digits(value)
  return v.length === 8 || v.length === 11
}

/** ОКВЭД: класс.подкласс с необязательной группой/подгруппой — NN.NN, NN.NN.N или NN.NN.NN
 * (напр. «85.42», «85.42.9», «85.42.90»). Паритет с BE `domain.ValidOKVED` и прототипом. */
export function isValidOkved(value: string): boolean {
  return /^\d{2}\.\d{2}(\.\d{1,2})?$/.test(value.trim())
}

/** email: непустая часть до первой «@» и точка после неё. Паритет с BE `domain.ValidEmailSimple`. */
export function isValidEmail(value: string): boolean {
  const v = value.trim()
  const at = v.indexOf('@')
  if (at <= 0 || at === v.length - 1) return false
  return v.slice(at + 1).includes('.')
}

/** Телефон: «+7» и 10 цифр после него. */
export function isValidPhone(value: string): boolean {
  const v = value.replace(/[\s()-]/g, '')
  return /^\+7\d{10}$/.test(v)
}

/** Юр. адрес: индекс (6 цифр) + минимум 4 части через запятые. */
export function isValidLegalAddress(value: string): boolean {
  const v = value.trim()
  if (!/\b\d{6}\b/.test(v)) return false
  return v.split(',').map((p) => p.trim()).filter(Boolean).length >= 4
}

/**
 * СНИЛС: 11 цифр с контрольной суммой по первым 9 (зеркало BE `domain.ValidSNILS`).
 * Контроль = взвешенная сумма первых 9 цифр (веса 9..1); при 100/101 → 0; при >101 → mod 101
 * (если 100/101 → 0). Сравнивается с двумя последними цифрами как числом.
 */
export function isValidSnils(value: string): boolean {
  const d = digits(value).split('').map(Number)
  if (d.length !== 11) return false
  let sum = 0
  for (let i = 0; i < 9; i++) sum += d[i] * (9 - i)
  let control = sum
  if (sum === 100 || sum === 101) {
    control = 0
  } else if (sum > 101) {
    control = sum % 101
    if (control === 100 || control === 101) control = 0
  }
  return control === d[9] * 10 + d[10]
}

/**
 * ФИО: минимум 3 слова, без инициалов-точек (зеркало BE `domain.ValidFIO`, добавляемого в Блоке 4).
 * «Иванов Иван Иванович» — ок; «Иванов И. И.» / «Иванов И.И.» / «Иванов Иван» — нет.
 */
export function validateFio(value: string): boolean {
  const words = value.trim().split(/\s+/).filter(Boolean)
  if (words.length < 3) return false
  return words.every((w) => !w.includes('.'))
}

/**
 * Сменщики (§10.1, p2-company-structure; зеркало BE `domain.ValidShiftPartners`):
 * обязательны ТОЛЬКО при включённом признаке сменного рабочего места.
 */
export function validShiftPartners(isShift: boolean, partnerCount: number): boolean {
  if (!isShift) return true
  return partnerCount > 0
}

/** Сообщения об ошибке по полю (дословно из company-profile-and-contacts). */
export const PROFILE_MESSAGES = {
  nameFull: 'Укажите полное наименование',
  nameShort: 'Укажите сокращённое наименование',
  inn: 'Проверьте ИНН (10 или 12 цифр)',
  kpp: 'Для юр. лица обязателен КПП (9 цифр)',
  /** КПП у не-юрлица (ИНН≠10): проверяется только формат при непустом значении. */
  kppFormat: 'Проверьте КПП (9 цифр)',
  ogrn: 'Проверьте ОГРН (13 или 15 цифр)',
  okpo: 'Проверьте ОКПО (8 или 10 цифр)',
  oktmo: 'Проверьте ОКТМО (8 или 11 цифр)',
  okved: 'Проверьте ОКВЭД (например 85.42, 85.42.9 или 85.42.90)',
  email: 'Проверьте email (нужны «@» и точка)',
  phone: 'Номер должен начинаться с +7 и содержать 10 цифр',
  legalAddress: 'Проверьте адрес (индекс и минимум 4 части через запятые)',
  directorName: 'Укажите руководителя',
  directorPosition: 'Укажите должность руководителя',
} as const

export interface ProfileDraft {
  nameFull: string
  nameShort: string
  inn: string
  kpp: string
  ogrn: string
  okpo: string
  oktmo: string
  okved: string
  /** ОКОГУ — скрытое passthrough-поле: сохраняется при правке, в UI не показывается. */
  okogu: string
  legalAddress: string
  email: string
  /** Телефон организации (формат +7 при наличии). */
  phone: string
  directorName: string
  directorPosition: string
}

export type ProfileErrors = Partial<Record<keyof ProfileDraft, string>>

/**
 * Валидирует черновик реквизитов. Возвращает карту поле→сообщение (пустая — валидно).
 * Обязательный набор ТОЧНО совпадает с серверной `isCompanyProfileComplete`
 * (backend `dto_company.go`), чтобы «форма заполнена» ⇔ `profileComplete=true` —
 * иначе онбординг-модалка появлялась бы снова после успешного сохранения формы.
 * Финальный согласованный набор (код-ревью p2-company-profile): полное наименование,
 * ИНН, ОГРН, юр.адрес, email, руководитель + должность. Сокращённое наименование и
 * телефон — НЕобязательны. Пустые необязательные поля (ОКПО/ОКТМО/ОКВЭД/КПП у ИП) не
 * проверяются; КПП обязателен только для юрлица (ИНН = 10 цифр).
 */
export function validateProfile(d: ProfileDraft): ProfileErrors {
  const errors: ProfileErrors = {}

  if (!d.nameFull.trim()) errors.nameFull = PROFILE_MESSAGES.nameFull
  if (!d.directorName.trim()) errors.directorName = PROFILE_MESSAGES.directorName
  if (!d.directorPosition.trim()) errors.directorPosition = PROFILE_MESSAGES.directorPosition

  if (!isValidInn(d.inn)) errors.inn = PROFILE_MESSAGES.inn
  // КПП обязателен и проверяется только у юрлица (ИНН = 10 цифр).
  if (isLegalEntityInn(d.inn) && !isValidKpp(d.kpp)) errors.kpp = PROFILE_MESSAGES.kpp
  if (!isValidOgrn(d.ogrn)) errors.ogrn = PROFILE_MESSAGES.ogrn
  if (d.okpo.trim() && !isValidOkpo(d.okpo)) errors.okpo = PROFILE_MESSAGES.okpo
  if (d.oktmo.trim() && !isValidOktmo(d.oktmo)) errors.oktmo = PROFILE_MESSAGES.oktmo
  if (d.okved.trim() && !isValidOkved(d.okved)) errors.okved = PROFILE_MESSAGES.okved
  if (!isValidEmail(d.email)) errors.email = PROFILE_MESSAGES.email
  // Телефон необязателен; проверяется только при непустом значении.
  if (d.phone.trim() && !isValidPhone(d.phone)) errors.phone = PROFILE_MESSAGES.phone
  if (!isValidLegalAddress(d.legalAddress)) errors.legalAddress = PROFILE_MESSAGES.legalAddress

  return errors
}

/**
 * Дополнительная обязательность реквизитов (IC-3, план фазы 2, реш.4): ОКПО/ОКВЭД/ОКТМО
 * обязательны ТОЛЬКО в контексте СОУТ (СОУТ-скоупный проп формы + серверный submit-гейт),
 * а НЕ глобально — иначе сломался бы онбординг/`profileComplete` (см. `isCompanyProfileComplete`).
 * Флаги задаёт вызывающий (SoutWizard → true); проверка накладывается ПОВЕРХ `validateProfile`,
 * не меняя саму `validateProfile`. Формат по-прежнему строгий (ОКВЭД — реш.7, не ослаблять).
 */
export interface ExtraRequisiteRequirement {
  okpo?: boolean
  okved?: boolean
  oktmo?: boolean
}

/**
 * Ошибки обязательности доп-реквизитов (пустая карта — всё заполнено/не требуется). Сообщение
 * — как у формата, но по причине «обязательно для СОУТ». Проверяет обязательность (непустоту)
 * поверх форматных проверок `validateProfile`; при непустом-но-кривом значении форматную
 * ошибку уже вернёт `validateProfile`, поэтому здесь достаточно проверки заполненности.
 */
export function validateExtraRequisites(
  d: Pick<ProfileDraft, 'okpo' | 'okved' | 'oktmo'>,
  require: ExtraRequisiteRequirement,
): ProfileErrors {
  const errors: ProfileErrors = {}
  if (require.okpo && !d.okpo.trim()) errors.okpo = 'ОКПО обязателен для СОУТ (8 или 10 цифр)'
  if (require.okved && !d.okved.trim()) errors.okved = 'ОКВЭД обязателен для СОУТ (например 85.42, 85.42.9 или 85.42.90)'
  if (require.oktmo && !d.oktmo.trim()) errors.oktmo = 'ОКТМО обязателен для СОУТ (8 или 11 цифр)'
  return errors
}

/** Реквизиты для «серверной» проверки (зеркало BE requisiteValues). */
export interface RequisiteDraft {
  inn: string
  kpp: string
  ogrn: string
  okpo: string
  oktmo: string
  okved: string
  email: string
  phone: string
  legalAddress: string
}

export type RequisiteErrors = Partial<Record<keyof RequisiteDraft, string>>

/**
 * Валидирует реквизиты ровно «как сервер» (зеркало BE `validateRequisites`, requisites.go):
 * формат проверяется ТОЛЬКО при непустом значении; КПП обязателен лишь для юрлица
 * (ИНН ровно 10 цифр) — иначе проверяется формат при наличии. Пустые реквизиты валидны
 * (клиента можно создать с одним наименованием + логином, без реквизитов).
 *
 * Отличие от `validateProfile`: здесь НЕТ обязательности наименования/ИНН/ОГРН/email/адреса —
 * это сценарий СОЗДАНИЯ клиента, где реквизиты опциональны (правка профиля — отдельно).
 */
export function validateRequisites(d: RequisiteDraft): RequisiteErrors {
  const errors: RequisiteErrors = {}

  if (d.inn.trim() && !isValidInn(d.inn)) errors.inn = PROFILE_MESSAGES.inn
  // КПП: обязателен для юрлица (ИНН=10); иначе — только формат при непустом значении.
  if (isLegalEntityInn(d.inn)) {
    if (!isValidKpp(d.kpp)) errors.kpp = PROFILE_MESSAGES.kpp
  } else if (d.kpp.trim() && !isValidKpp(d.kpp)) {
    errors.kpp = PROFILE_MESSAGES.kppFormat
  }
  if (d.ogrn.trim() && !isValidOgrn(d.ogrn)) errors.ogrn = PROFILE_MESSAGES.ogrn
  if (d.okpo.trim() && !isValidOkpo(d.okpo)) errors.okpo = PROFILE_MESSAGES.okpo
  if (d.oktmo.trim() && !isValidOktmo(d.oktmo)) errors.oktmo = PROFILE_MESSAGES.oktmo
  if (d.okved.trim() && !isValidOkved(d.okved)) errors.okved = PROFILE_MESSAGES.okved
  if (d.email.trim() && !isValidEmail(d.email)) errors.email = PROFILE_MESSAGES.email
  if (d.phone.trim() && !isValidPhone(d.phone)) errors.phone = PROFILE_MESSAGES.phone
  if (d.legalAddress.trim() && !isValidLegalAddress(d.legalAddress)) {
    errors.legalAddress = PROFILE_MESSAGES.legalAddress
  }

  return errors
}
